/**
 * 長押しジェスチャーフック
 * モバイルでの長押しを検出し、コールバックを呼び出す
 */

import { useCallback, useRef } from 'react';

interface UseLongPressOptions {
  onLongPress: () => void;
  /** 長押し判定までのミリ秒（デフォルト: 500ms） */
  delay?: number;
  /** 長押しキャンセルまでの移動量（デフォルト: 10px） */
  moveThreshold?: number;
}

/**
 * 長押し発火時に document へ通知するイベント名。
 * NavigationLoading が「長押し直後の click はナビゲーションではない」と判定するために
 * 購読する（押下時間の推測ではなく実際の長押し判定を共有する。#223）。
 */
export const LONG_PRESS_EVENT = 'app:longpress';

interface UseLongPressReturn {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  /** 長押しが発火したかどうかを確認し、trueならリセットして返す */
  consumeLongPress: () => boolean;
}

export function useLongPress({
  onLongPress,
  delay = 500,
  moveThreshold = 10,
}: UseLongPressOptions): UseLongPressReturn {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const didLongPressRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      didLongPressRef.current = false;
      const touch = e.touches[0];
      startPosRef.current = { x: touch.clientX, y: touch.clientY };

      timerRef.current = setTimeout(() => {
        didLongPressRef.current = true;
        document.dispatchEvent(new CustomEvent(LONG_PRESS_EVENT));
        onLongPress();
      }, delay);
    },
    [onLongPress, delay]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!startPosRef.current) return;
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - startPosRef.current.x);
      const dy = Math.abs(touch.clientY - startPosRef.current.y);
      if (dx > moveThreshold || dy > moveThreshold) {
        clear();
      }
    },
    [moveThreshold, clear]
  );

  const onTouchEnd = useCallback(() => {
    clear();
  }, [clear]);

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // 長押し時のコンテキストメニューを抑制
      if (didLongPressRef.current) {
        e.preventDefault();
      }
    },
    []
  );

  /** 長押しが発火していたらtrueを返してリセットする */
  const consumeLongPress = useCallback(() => {
    if (didLongPressRef.current) {
      didLongPressRef.current = false;
      return true;
    }
    return false;
  }, []);

  return { onTouchStart, onTouchEnd, onTouchMove, onContextMenu, consumeLongPress };
}
